import * as React from "react";
import { useEffect, useState } from "react";
import { Grid, Paper, WithStyles } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { styles } from "./ProductOverview.styles";
import { ProductCategory } from "./productOverview/ProductCategory";
import { CenteredContainer } from "../components/CenteredContainer/CenteredContainer";
import { PageBanner } from "../components/PageBanner/PageBanner";
import { useDispatch, useSelector } from "react-redux";
import { ProductSidebar } from "./productOverview/ProductSidebar";
import { useHistory, useLocation } from "react-router-dom";
import { requestProducts } from "../../stores/products/ProductsAction";
import { findCategoryByName } from "./productOverview/ProductHelper";
import { BlueLink } from "../components/Links/BlueLink";

export interface ProductPageProps extends WithStyles<typeof styles> {
}

export const QUERY_PARAM_CATEGORY = "category";
export const QUERY_PARAM_SUBCATEGORY = "subcategory";
export const QUERY_PARAM_VENUE = "venue";

function useQuery(location) {
    const search = location.search;

    return React.useMemo(() => new URLSearchParams(search), [search]);
}

function scrollToTop() {
    window.scrollTo({top: 120, behavior: "smooth"});
}

function filter(products, filters: { venue: string; category: string; subcategory: string }) {
    return products.filter(p => p.visible
        && (filters.venue === "" || p.venue?.includes(filters.venue))
        && (filters.category === "" || p.category === filters.category)
        && (filters.subcategory === "" || p.subcategory === filters.subcategory));
}

function countVisibleProducts(filteredCategories, productInfo, filters) {
    let visibleProducts = 0;
    filteredCategories?.forEach(category => {
        visibleProducts += filter(productInfo.products, {
            ...filters,
            category: category.id
        }).length;
    });
    return visibleProducts;
}

interface IQueryParams
{
    category?: string;
    subcategory?: string;
    venue?: string;
}

function appendQuery(query: IQueryParams, location, history) {
    const queryParams = new URLSearchParams(location.search);
    if (query.category !== undefined) {
        query.category === "" ? queryParams.delete(QUERY_PARAM_CATEGORY) : queryParams.set(QUERY_PARAM_CATEGORY, query.category);
    }
    if (query.subcategory !== undefined) {
        query.subcategory === "" ? queryParams.delete(QUERY_PARAM_SUBCATEGORY) : queryParams.set(QUERY_PARAM_SUBCATEGORY, query.subcategory);
    }
    if (query.venue !== undefined) {
        query.venue === "" ? queryParams.delete(QUERY_PARAM_VENUE) : queryParams.set(QUERY_PARAM_VENUE, query.venue);
    }
    history.replace({
        search: queryParams.toString(),
    });
}

const ProductViewPure: React.FunctionComponent<ProductPageProps> = ({classes}: ProductPageProps) => {
    const location = useLocation();
    const query = useQuery(location);
    const history = useHistory();

    const category = query.get(QUERY_PARAM_CATEGORY) || "";
    const subcategory = query.get(QUERY_PARAM_SUBCATEGORY) || "";
    const venue = query.get(QUERY_PARAM_VENUE) || "";
    const [isA7, setA7] = useState(category?.includes("A7"));
    const [isSampleData, setSampleData] = useState(category?.includes("ANALYTICS_SAMPLE_DATA"));

    function handleViewAll() {
        history.replace({
            search: ""
        });
    }

    function handleCategoryChange(newCategory, isA7 = false, isSampleData = false) {
        appendQuery({category: newCategory, subcategory: ""}, location, history);
        setA7(isA7);
        setSampleData(isSampleData);
        if (isA7) {
            setSampleData(false);
        }
        if (isSampleData) {
            setA7(false);
        }
        scrollToTop();
    }

    function handleSubcategoryChange(newCategory, newSubcategory, isA7 = false, isSampleData = false) {
        appendQuery({category: newCategory, subcategory: newSubcategory}, location, history);
        setA7(isA7);
        setSampleData(isSampleData)
        if (newCategory === "") {
        } else {
        }
        scrollToTop();
    }

    function handleVenueChange(newVenue) {
        // reset category to not confuse with a7 products
        appendQuery({venue: newVenue}, location, history);
        if (isA7) {
            setA7(false);
        }
        // reset category to not confuse with sample data
        if (isSampleData) {
            setSampleData(false);
        }
        scrollToTop();
    }

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(requestProducts());
    }, [dispatch]);

    const productsInfo = useSelector((state) => state.products);
    const a7productsInfoFromState = useSelector((state) => state.a7products);

    // split the products delivered from the a7 endpoint into a7 products and sample products
    const a7productsInfo = {
        products: a7productsInfoFromState?.products?.filter(product => product.id.includes("A7")),
        categories: a7productsInfoFromState?.categories?.filter(category => category.id.includes("A7"))
    };

    const sampleProductsInfo = {
        products: a7productsInfoFromState?.products?.filter(product => !product.id.includes("A7")),
        categories: a7productsInfoFromState?.categories?.filter(category => !category.id.includes("A7"))
    };

    const filters = {
        category,
        subcategory,
        venue
    };

    const productsInfoToShow = isA7 ? a7productsInfo : (isSampleData ? sampleProductsInfo : productsInfo);

    const filteredCategories = productsInfoToShow?.categories?.filter(c => c.id === category || category === "");
    const numberOfFilteredProducts = countVisibleProducts(filteredCategories, productsInfoToShow, filters);

    return <div className={classes.container}>
        <PageBanner title="Data shop product overview" isPublic />
        <CenteredContainer>
            <Grid container spacing={0}>
                <Grid item xs={3}>
                    <ProductSidebar datashopProductsInfo={productsInfo}
                                    a7ProductsInfo={a7productsInfo}
                                    sampleProductsInfo={sampleProductsInfo}
                                    categoryActive={category}
                                    subcategoryActive={subcategory}
                                    venueActive={venue}
                                    handleViewAll={handleViewAll}
                                    setCategoryActive={handleCategoryChange}
                                    setSubcategoryActive={handleSubcategoryChange}
                                    setVenueActive={handleVenueChange} />
                </Grid>
                <Grid item xs={9} className={classes.productSection}>
                    {filteredCategories !== undefined && numberOfFilteredProducts === 0
                        ? <div>
                            <Paper className={classes.filterMessage}>
                                <div>Nothing found
                                    for <b>{findCategoryByName(productsInfoToShow?.categories, filters.category)?.name}</b> and <b>{filters.venue}</b>.
                                    <br />Please use a different filter.
                                </div>
                                <br/>
                                <BlueLink onClick={handleViewAll} variant="subtitle2">Clear all filters</BlueLink></Paper>
                        </div>
                        : filteredCategories?.map(category =>
                            <ProductCategory key={category.id}
                                             products={filter(productsInfoToShow.products, {
                                                 ...filters,
                                                 category: category.id
                                             })}
                                             isA7={isA7}
                                             isSampleData={isSampleData}
                                             subcategoryActive={subcategory}
                                             category={category}
                                             onClick={handleSubcategoryChange} />
                        )}
                </Grid>
            </Grid>
        </CenteredContainer>
    </div>;
};


export const ProductOverview = withStyles(styles)(ProductViewPure);
